import { Box, Typography } from "@mui/material";
import { WasteClassification } from "generated-graphql/graphql";

import { formatWasteStreamProfileNumber } from "../Facilities/Facility/formatWasteStreamProfileNumber";
import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";

/**
 * The subset of waste-stream grid fields shown as an at-a-glance summary in
 * the dialog — the current stream's info box (above the tabs) and each merge
 * target's card both render these, so a user can compare streams without
 * leaving the dialog. Every field is optional/nullable: the same shape is
 * satisfied by both grids' generated row types and by the merge-targets query.
 */
export interface WasteStreamSummaryFieldsData {
  eManifestProfileNumber?: string | null;
  vendorProfileNumber?: string | null;
  federalWasteCodes?: string[] | null;
  stateWasteCodes?: string[] | null;
  classification?: WasteClassification | null;
  formCode?: string | null;
  sourceCode?: string | null;
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

export function WasteStreamSummaryFields({
  data,
  includeSourceCode = false,
}: {
  data: WasteStreamSummaryFieldsData;
  /** Show the Source Code field (the current-stream box does; merge cards don't). */
  includeSourceCode?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))",
        gap: 1.5,
      }}
    >
      <Field label="Profile Number" value={formatWasteStreamProfileNumber(data)} />
      <Field label="Federal Waste Code" value={(data.federalWasteCodes ?? []).join(", ")} />
      <Field label="State Waste Code" value={(data.stateWasteCodes ?? []).join(", ")} />
      <Field label="Classification" value={prettyPrintWasteClassification(data.classification)} />
      <Field label="Form Code" value={data.formCode ?? ""} />
      {includeSourceCode && <Field label="Source Code" value={data.sourceCode ?? ""} />}
    </Box>
  );
}
